import type { CSSVariablesResolver } from "@mantine/core";

import { darkTokens, lightTokens } from "../tokens";
import { deriveTokens, type TokenName } from "./derive";
import { isSeeded, type Scheme, type ThemeFamily } from "./family";
import { mantineVariables } from "./mantine";

/** A family's tokens for one variant, as the app shows them: a card's preview reads them. */
export function familyTokens(family: ThemeFamily, scheme: Scheme): Record<TokenName, string> {
  if (isSeeded(family)) return deriveTokens(family[scheme], scheme);
  return scheme === "dark" ? darkTokens : lightTokens;
}

const resolvers = new Map<string, CSSVariablesResolver>();

/**
 * Writes a family's two variants into Mantine's light and dark slots (spec web/themes-engine).
 * Created.ai writes tokens.ts as it always has, and nothing of Mantine's; a seeded family writes
 * its derived tokens and Mantine's palettes for each slot. One resolver per family, made once, so
 * the provider's styles change only when the family does.
 */
export function cssVariablesResolverFor(family: ThemeFamily): CSSVariablesResolver {
  const known = resolvers.get(family.id);
  if (known !== undefined) return known;
  let resolver: CSSVariablesResolver;
  if (isSeeded(family)) {
    const light = { ...deriveTokens(family.light, "light"), ...mantineVariables(family.light, "light") };
    const dark = { ...deriveTokens(family.dark, "dark"), ...mantineVariables(family.dark, "dark") };
    resolver = () => ({ variables: {}, light, dark });
  } else {
    resolver = () => ({ variables: {}, light: lightTokens, dark: darkTokens });
  }
  resolvers.set(family.id, resolver);
  return resolver;
}
